import { ComponentRef, Inject, Injectable, Type, ViewContainerRef } from '@angular/core';
import { LocalStorageService } from '../../core/service/local-storage.service';
import { WidgetNotifyService } from './widget-notify.service';
import { UserComponent } from '../../core/component/user/user.component';
import { PrintComponent } from '../../component/tools/print/print.component';
import { MeasureComponent } from '../../component/tools/measure/measure.component';
import { DrawComponent } from '../../component/tools/draw/draw.component';
import { AppInfoComponent } from '../../core/component/app-info/app-info.component';
import { MassStabSliderComponent } from '../../component/tools/massstab-slider/massstab-slider.component';
import { DetailViewComponent } from '../../component/detail-view/detail-view.component';
import { GoToComponent } from '../../component/tools/go-to/go-to.component';
import { CONSTANTS } from '../../config/constants.module';
import { ShareComponent } from 'src/app/component/tools/share/share.component';
import { MainMapService } from './main-map.service';
import { SearchHektometerComponent } from 'src/app/component/tools/search-hektometer/search-hektometer.component';
import { SearchParzelleComponent } from '../../component/tools/search-parzelle/search-parzelle.component';
import { GeoPortaleComponent } from '../../component/tools/geo-portale/geo-portale.component';
import { GoogleMapsToolComponent } from '../../component/tools/google-maps-tool-module/components/google-maps-tool/google-maps-tool.component';

/**
 * @copyright Schweizerische Bundesbahnen SBB
 * @class WidgetService
 * @version 0.0.1
 * @since November 2017
 * @author Tuan La
 * @description The WidgetService is used for managing dynamic created
 * widgets.
 */
@Injectable({
  providedIn: 'root',
})
export class WidgetService {
  // The reference to parent DOM element, in which widgets will be rendered
  private _rootViewContainer: ViewContainerRef;
  // Reference to current created dynamic components
  private readonly _currentComponentRefs: ComponentRef<any>[];

  constructor(
    private _widgetNotifyService: WidgetNotifyService,
    private _localStorageService: LocalStorageService,
    private _mainMapService: MainMapService,
    @Inject(CONSTANTS) private CONST: any
  ) {
    // initialize component pool
    this._currentComponentRefs = new Array<ComponentRef<any>>();
    // Listen on component destroy subject. If a component on the DOM is destroyed, remove reference ID
    this._widgetNotifyService.onComponentDestroySubject.subscribe(componentID => {
      this.removeWidgetFromPool(componentID);
    });
    // Create Detailview dynamic widget on identify feature
    this._widgetNotifyService.onShowFeatureSubject.subscribe(result => {
      this.createDetailViewWidget(result);
    });
  }

  /**
   * @name loadWidgetsFromLocalStorage
   * @description loads the saved widget-ids from the local storage
   */
  public loadWidgetsFromLocalStorage() {
    const widgetIds = this._localStorageService.widgetIds;
    if (!widgetIds) {
      return;
    }
    widgetIds.forEach(widgetId => {
      this.createWidget(false, widgetId);
    });
  }

  public createWidget(expand: boolean, widgetId: string) {
    switch (widgetId) {
      case this.CONST.WIDGET_ID.UserComponent:
        this.createComponent(expand, widgetId, UserComponent);
        break;
      case this.CONST.WIDGET_ID.SearchParzelleComponent:
        this.createComponent(expand, widgetId, SearchParzelleComponent);
        break;
      case this.CONST.WIDGET_ID.SearchParzelleCHComponent:
        this.createComponent(expand, widgetId, SearchParzelleComponent);
        break;
      case this.CONST.WIDGET_ID.SearchHektometerComponent:
        this.createComponent(expand, widgetId, SearchHektometerComponent);
        break;
      case this.CONST.WIDGET_ID.GeoPortaleComponent:
        this.createComponent(expand, widgetId, GeoPortaleComponent);
        break;
      case this.CONST.WIDGET_ID.PrintComponent:
        this.createComponent(expand, widgetId, PrintComponent);
        break;
      case this.CONST.WIDGET_ID.MeasureComponent:
        this.createComponent(expand, widgetId, MeasureComponent);
        break;
      case this.CONST.WIDGET_ID.AppInfoComponent:
        this.createComponent(expand, widgetId, AppInfoComponent);
        break;
      case this.CONST.WIDGET_ID.MassStabSliderComponent:
        this.createComponent(expand, widgetId, MassStabSliderComponent);
        break;
      case this.CONST.WIDGET_ID.DrawComponent:
        this.createComponent(expand, widgetId, DrawComponent);
        break;
      case this.CONST.WIDGET_ID.GoToComponent:
        this.createComponent(expand, widgetId, GoToComponent);
        break;
      case this.CONST.WIDGET_ID.ShareComponent:
        this.createComponent(expand, widgetId, ShareComponent);
        break;
      case this.CONST.WIDGET_ID.GoogleMapsComponent:
        this.createComponent(expand, widgetId, GoogleMapsToolComponent);
        break;
    }
  }

  /**
   * @function checkComponentExpanded
   * @param {string} widgetID
   * @returns {boolean}
   * @description Check if the exists component expanded on not
   */
  public checkComponentExpanded(widgetID: string): boolean {
    if (!this._currentComponentRefs || this._currentComponentRefs.length === 0) {
      return false;
    }
    const _widget = this._currentComponentRefs.filter(f => f.instance.id === widgetID)[0];
    if (!_widget) {
      return false;
    }
    const expandWidget = _widget.instance.expandWidget;
    if (!expandWidget.hasOwnProperty('expanded')) {
      console.error(`Widget '${widgetID}' has no 'expanded' property defined!`);
      return false;
    }
    return expandWidget.expanded;
  }

  /**
   * @function setRootViewContainerRef
   * @param {ViewContainerRef} viewContainerRef
   * @description Set reference to parent DOM, in which components will be rendered
   */
  public setRootViewContainerRef(viewContainerRef: ViewContainerRef) {
    this._rootViewContainer = viewContainerRef;
  }

  /**
   * @function createDetailViewWidget
   * @description Create new dynamic component and inject as child
   * of predefined parent DOM element
   */
  public createDetailViewWidget(result: any) {
    if (!this.checkComponentExists(this.CONST.WIDGET_ID.DetailViewComponent)) {
      const detailViewComponentRef = this._rootViewContainer.createComponent(DetailViewComponent);
      detailViewComponentRef.instance.setInitValues(this.CONST.WIDGET_ID.DetailViewComponent, true);
      this.registerWidget(detailViewComponentRef);
      detailViewComponentRef.instance.setFeatureParams(result);
    } else {
      // iterate alle offene dynamic widgets
      this._currentComponentRefs.forEach(c => {
        // hole DetailView dynamic widget
        if (c.instance.id === this.CONST.WIDGET_ID.DetailViewComponent) {
          const componentIndex = this._currentComponentRefs.indexOf(c);
          if (componentIndex > -1) {
            // entferne current componentRef aus dem Array
            this._currentComponentRefs.splice(componentIndex, 1);

            // Component self destroy und erstelle neues DetailView dynamic component mit dem gegebenen Result identifier
            c.instance.destroyWidget();
            c.destroy();

            this.createDetailViewWidget(result);
            return false;
          }
        }
      });
    }
  }

  /**
   * @function checkComponentExists
   * @param {string} widgetID
   * @returns {boolean}
   * @description Check if the created component already exists in the
   * DOM. Each component type can be created only one time in the DOM.
   */
  public checkComponentExists(widgetID: string): boolean {
    if (!this._currentComponentRefs || this._currentComponentRefs.length === 0) {
      return false;
    }

    let exists = false;
    // Remove component Ref from current open widgets
    this._currentComponentRefs.forEach(c => {
      // hole DetailView dynamic widget
      if (c.instance.id === widgetID) {
        exists = true;
        return false;
      }
    });

    return exists;
  }

  /* Private methods
  ================== */
  /**
   * @function createComponent()
   * @description Create new dynamic component and inject as child of predefined parent DOM element
   */
  private createComponent(expand: boolean, widgetId: string, component: Type<any>) {
    if (!this.checkComponentExists(widgetId)) {
      const componentRef = this._rootViewContainer.createComponent(component);
      componentRef.instance.setInitValues(widgetId, expand);
      this.registerWidget(componentRef);
    } else {
      this.expandWidget(widgetId);
    }
  }

  /**
   * @name saveWidgetsToLocalStorage
   * @description Saves the loaded widget-ids into local storage
   */
  private saveWidgetsToLocalStorage(widgetID: string) {
    this._localStorageService.widgetId = widgetID;
  }

  /**
   * @name registerWidget
   * @param componentRef
   */
  private registerWidget(componentRef: ComponentRef<any>) {
    this._rootViewContainer.insert(componentRef.hostView);
    this._currentComponentRefs.push(componentRef);
  }

  private removeWidgetFromPool(componentID: string) {
    // Remove component Ref from current open widgets
    this._currentComponentRefs.forEach(c => {
      // hole DetailView dynamic widget
      if (c.instance.id === componentID) {
        const componentIndex = this._currentComponentRefs.indexOf(c);
        // entferne current componentRef aus dem Array
        this._currentComponentRefs.splice(componentIndex, 1);
        // remove Esri-Widget from temporary active widget pool in mainMapService
        this._mainMapService.removeWidgetFromPool(componentID);
        // Get the main map and remove the widget from the main map UI
        c.instance.removeWidgetFromMainMapUI();
        // Get Angular component instance and destroy the Esri-Expand Widget saved in the Angular component
        c.instance.destroyWidget();
        // Destroy the angular component itself (c is of type ComponentRef)
        c.destroy();
        // remove destroyed component in localstorage if needed
        this._localStorageService.removeWidgetId(componentID);
        return false;
      }
    });
  }

  /**
   * @name expandWidget
   * @description if component exists expanded it
   * @param _componentId
   */
  private expandWidget(_componentId: string) {
    const _widget = this._currentComponentRefs.filter(f => f.instance.id === _componentId)[0];
    if (!_widget) {
      return;
    }
    _widget.instance._expandWidget.expand();
  }
}
